import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PictureInPictureAlt as PipIcon,
  DesktopWindows as DesktopIcon,
  Language as WebAppIcon,
  MenuBook as IntroIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  FiberManualRecord as DotIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  presentationService,
  PresentationData,
  PresentationStep,
} from '../../services/presentation.service';
import { DesktopInfo } from '../../services/guacamole.service';
import { useSessionSocket } from '../../hooks/useSessionSocket';
import { BlockRenderer } from '../../components/workshop/blocks';
import ReactionFeed, { useReactionCollector } from '../../components/instructor/ReactionFeed';
import { browserNotifications } from '../../utils/browserNotifications';
import { usePictureInPicture } from '../../hooks/usePictureInPicture';
import { useProgressPulse } from '../../hooks/useProgressPulse';
import PiPPortal from '../../components/pip/PiPPortal';
import PiPWidget, { PiPHelpRequest, PiPReaction } from '../../components/pip/PiPWidget';
import { WsHelpRequest, WsHelpResolved, WsHelpMessage, WsSessionChatMessage } from '../../services/socket.service';
import { socketService } from '../../services/socket.service';
import { useAuth } from '../../hooks/useAuth';
import { helpRequestService } from '../../services/help-request.service';
import axios from 'axios';

const stepTypeIcon: Record<string, React.ReactElement> = {
  remote_desktop: <DesktopIcon sx={{ fontSize: 14 }} />,
  web_app: <WebAppIcon sx={{ fontSize: 14 }} />,
  introduction: <IntroIcon sx={{ fontSize: 14 }} />,
};

const PresentationMode: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PresentationData | null>(null);
  const [desktopInfo, setDesktopInfo] = useState<DesktopInfo | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [instructorHWM, setInstructorHWM] = useState(0);
  const [stepListExpanded, setStepListExpanded] = useState(false);

  // Split panel state
  const [splitRatio, setSplitRatio] = useState<number>(() => {
    const saved = localStorage.getItem('praxis-present-split');
    return saved ? Number(saved) : 35;
  });
  const [isInstructionsCollapsed, setIsInstructionsCollapsed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  // Guacamole iframe ref
  const guacIframeRef = useRef<HTMLIFrameElement>(null);
  const guacClientRef = useRef<any>(null);

  // Clipboard dialog state
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [remoteClipboardText, setRemoteClipboardText] = useState('');

  // Snackbar notification
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'info' });
  const showNotification = useCallback(
    (message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => {
      setNotification({ open: true, message, severity });
    },
    [],
  );

  // Reaction feed state
  const { reactions, addReaction } = useReactionCollector();

  // PiP floating widget
  const { isSupported: pipSupported, isOpen: pipOpen, pipWindow, open: openPip, close: closePip } = usePictureInPicture({ width: 264, height: 186 });

  // Real-time step progress for PiP widget
  const { stepData, totalStudents, studentPositions, handleProgressUpdate } = useProgressPulse(
    sessionId || '',
    data?.steps.map(s => ({ id: s.id, orderIndex: s.orderIndex })) || [],
  );

  // Session chat state
  const [chatMessages, setChatMessages] = useState<WsSessionChatMessage[]>([]);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  const handleSendChatMessage = useCallback((message: string) => {
    if (!sessionId) return;
    socketService.sendChatMessage(sessionId, message);
  }, [sessionId]);

  const handleChatViewed = useCallback(() => {
    setChatUnreadCount(0);
  }, []);

  // Aggregate reactions for PiP widget — 30-second rolling window
  const pipReactions: PiPReaction[] = useMemo(() => {
    const cutoff = Date.now() - 30_000;
    const recentReactions = reactions.filter(r => new Date(r.timestamp).getTime() > cutoff);
    const grouped = new Map<string, Set<string>>();
    for (const r of recentReactions) {
      if (!grouped.has(r.emoji)) grouped.set(r.emoji, new Set());
      grouped.get(r.emoji)!.add(r.userName || 'Student');
    }
    return Array.from(grouped.entries())
      .map(([emoji, names]) => ({ emoji, count: names.size, names: Array.from(names) }))
      .sort((a, b) => b.count - a.count);
  }, [reactions]);

  // Active help requests for PiP widget
  const [activeHelpRequests, setActiveHelpRequests] = useState<PiPHelpRequest[]>([]);

  const handleHelpRequest = useCallback((d: WsHelpRequest) => {
    if (d.status !== 'open') return;
    const entry: PiPHelpRequest = {
      helpRequestId: d.id,
      studentName: d.studentName || 'A student',
      stepTitle: d.stepTitle,
      stepOrderIndex: d.stepOrderIndex,
      message: d.message || 'Needs help',
      status: 'new',
      createdAt: Date.now(),
    };
    setActiveHelpRequests((prev) => [...prev, entry]);
  }, []);

  const handleHelpResolved = useCallback((d: WsHelpResolved) => {
    setActiveHelpRequests((prev) => prev.filter((hr) => hr.helpRequestId !== d.id));
  }, []);

  const handleQuickReply = useCallback((helpRequestId: string, message: string) => {
    helpRequestService.addMessage(helpRequestId, message).catch(() => {});
  }, []);

  const handleResolveHelp = useCallback((helpRequestId: string) => {
    helpRequestService.resolve(helpRequestId).catch(() => {});
    setActiveHelpRequests((prev) => prev.filter((hr) => hr.helpRequestId !== helpRequestId));
  }, []);

  // WebSocket — join session room, feed events into state
  const { connectionStatus } = useSessionSocket({
    sessionId: sessionId || '',
    onProgressUpdate: handleProgressUpdate,
    onHelpRequest: handleHelpRequest,
    onHelpResolved: handleHelpResolved,
    onHelpMessage: (d: WsHelpMessage) => {
      if (d.userRole === 'instructor') {
        setActiveHelpRequests(prev =>
          prev.map(hr =>
            hr.helpRequestId === d.helpRequestId
              ? { ...hr, status: 'replied' as const, lastReply: d.message }
              : hr
          )
        );
      }
    },
    onBroadcast: (d) =>
      console.log('[PresentationMode] broadcast:', d),
    onSessionReaction: addReaction,
    onSessionChatMessage: (d) => {
      setChatMessages(prev => [...prev, d]);
      setChatUnreadCount(prev => prev + 1);
    },
  });

  // Request browser notification permission for help request alerts
  useEffect(() => {
    browserNotifications.requestPermission();
  }, []);

  // Update instructor high-water mark on step navigation
  useEffect(() => {
    setInstructorHWM(prev => Math.max(prev, currentStepIndex));
  }, [currentStepIndex]);

  // Load presentation data + instructor desktop
  useEffect(() => {
    if (!sessionId) return;
    const load = async () => {
      try {
        const [presData, desktop] = await Promise.all([
          presentationService.getPresentationData(sessionId),
          presentationService
            .getInstructorDesktop(sessionId)
            .catch(() => null),
        ]);
        setData(presData);
        setDesktopInfo(desktop);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          showNotification('Presentation mode is available to the session owner only', 'info');
          navigate(`/instructor/live/${sessionId}`, { replace: true });
        } else {
          showNotification('Failed to load presentation data', 'error');
          navigate(`/instructor/session/${sessionId}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId, navigate, showNotification]);

  // Persist split ratio
  useEffect(() => {
    localStorage.setItem('praxis-present-split', String(splitRatio));
  }, [splitRatio]);

  // Keyboard shortcut: Ctrl+B to toggle collapse
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'b' || e.key === '\\')
      ) {
        if (document.activeElement?.tagName === 'IFRAME') return;
        e.preventDefault();
        setIsInstructionsCollapsed((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Drag handler for split resize
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);

    const handleDragMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const newRatio =
        ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setSplitRatio(Math.max(20, Math.min(60, newRatio)));
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  // Focus the Guacamole iframe for keyboard input
  const focusIframeContent = useCallback(() => {
    try {
      guacIframeRef.current?.contentWindow?.focus();
    } catch (_) {
      /* cross-origin safety */
    }
  }, []);

  // Access the Guacamole client object from the iframe's JS context
  const getGuacClient = useCallback(() => {
    try {
      const iframe = guacIframeRef.current;
      if (!iframe?.contentWindow) return null;
      const iframeWin = iframe.contentWindow as any;
      if (iframeWin.__workshubClient) {
        guacClientRef.current = iframeWin.__workshubClient;
        return guacClientRef.current;
      }
      if (!iframe.contentDocument) return null;
      const angular = iframeWin.angular;
      if (!angular) return null;
      for (const selector of ['.client-main', '.display', '.client-tile', '.tiled-client-grid']) {
        const el = iframe.contentDocument.querySelector(selector);
        if (!el) continue;
        const scope = angular.element(el).scope();
        const client = scope?.client?.client || scope?.managedClient?.client || null;
        if (client && typeof client.sendKeyEvent === 'function') {
          guacClientRef.current = client;
          return client;
        }
      }
      return null;
    } catch { return null; }
  }, []);

  // Send text to the remote session's clipboard via Guacamole client API
  const sendTextToRemoteClipboard = useCallback((text: string) => {
    const iframe = guacIframeRef.current;
    if (!iframe?.contentWindow) return false;
    try {
      const client = getGuacClient();
      if (client) {
        const GuacLib = (iframe.contentWindow as any).Guacamole;
        if (!GuacLib) return false;
        const stream = client.createClipboardStream('text/plain');
        const writer = new GuacLib.StringWriter(stream);
        writer.sendText(text);
        writer.sendEnd();
        return true;
      }
    } catch (err) {
      console.warn('Guacamole clipboard sync failed:', err);
    }
    return false;
  }, [getGuacClient]);

  // Fallback paste: simulate typing each character via Guacamole key events
  const simulateTyping = useCallback((text: string) => {
    const client = getGuacClient();
    if (!client) return false;
    for (let i = 0; i < text.length; i++) {
      const code = text.charCodeAt(i);
      const keysym = code >= 0x0020 && code <= 0x007E ? code : 0x01000000 | code;
      client.sendKeyEvent(1, keysym);
      client.sendKeyEvent(0, keysym);
    }
    return true;
  }, [getGuacClient]);

  // Try reading clipboard directly; fall back to dialog if clipboard read fails or is empty
  const handlePasteToRemote = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        sendTextToRemoteClipboard(text);
        simulateTyping(text);
        showNotification('Text pasted into remote session', 'success');
        setTimeout(focusIframeContent, 100);
        return;
      }
    } catch {
      // Clipboard read failed — fall back to dialog
    }
    setPasteText('');
    setPasteDialogOpen(true);
  }, [sendTextToRemoteClipboard, simulateTyping, showNotification, focusIframeContent]);

  // Send text from paste dialog to remote session
  const handlePasteDialogSend = useCallback(() => {
    const text = pasteText;
    setPasteDialogOpen(false);
    setPasteText('');
    if (!text) {
      showNotification('No text to paste', 'info');
      return;
    }
    const client = getGuacClient();
    if (!client) {
      showNotification('Remote connection not ready — try again in a moment', 'warning');
      return;
    }
    sendTextToRemoteClipboard(text);
    const typed = simulateTyping(text);
    if (typed) {
      showNotification('Text pasted into remote session', 'success');
    } else {
      showNotification('Could not send key events to remote', 'warning');
    }
    setTimeout(focusIframeContent, 100);
  }, [pasteText, getGuacClient, sendTextToRemoteClipboard, simulateTyping, showNotification, focusIframeContent]);

  // Read remote clipboard from iframe and open copy dialog
  const handleCopyFromRemote = useCallback(() => {
    const iframe = guacIframeRef.current;
    const text = (iframe?.contentWindow as any)?.__workshubRemoteClipboard || '';
    setRemoteClipboardText(text);
    setCopyDialogOpen(true);
  }, []);

  // Copy remote clipboard text to local clipboard
  const handleCopyDialogCopy = useCallback(() => {
    if (remoteClipboardText) {
      navigator.clipboard.writeText(remoteClipboardText).then(() => {
        showNotification('Copied to your clipboard!', 'success');
        setCopyDialogOpen(false);
      }).catch(() => {
        showNotification('Clipboard write failed — select the text and copy manually', 'warning');
      });
    }
  }, [remoteClipboardText, showNotification]);

  // Inject CSS + clipboard interceptor into Guacamole iframe
  const handleIframeLoad = useCallback(() => {
    focusIframeContent();
    const iframe = guacIframeRef.current;
    if (!iframe) return;

    try {
      const iframeDoc = iframe.contentDocument;
      if (!iframeDoc) return;

      // CSS to fill viewport and hide Guacamole menu
      const style = iframeDoc.createElement('style');
      style.textContent = `
        .viewport, .client-view, .client-view-content, .client-body,
        .tiled-client-grid, .tiled-client-row, .tiled-client-cell,
        .client-tile, .client-main {
          width: 100% !important;
          height: 100% !important;
        }
        .displayOuter {
          width: 100% !important;
          height: 100% !important;
          overflow: hidden !important;
          background: #1e1e2e !important;
        }
        .displayMiddle {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .menu { display: none !important; }
      `;
      iframeDoc.head.appendChild(style);

      // Clipboard + keyboard script — runs inside the iframe context
      const script = iframeDoc.createElement('script');
      script.textContent = `
(function() {
  var interval = setInterval(function() {
    try {
      if (!window.angular || !window.Guacamole) return;
      var mainEl = document.querySelector('.client-main');
      if (!mainEl) return;
      var scope = angular.element(mainEl).scope();
      if (!scope) return;
      var client = (scope.client && scope.client.client) || null;
      if (!client) return;
      clearInterval(interval);
      window.__workshubClient = client;
      console.log('[Praxis] Instructor Guacamole client hooked');

      function syncClipboard(text) {
        try {
          var stream = client.createClipboardStream('text/plain');
          var writer = new Guacamole.StringWriter(stream);
          writer.sendText(text);
          writer.sendEnd();
          return true;
        } catch(e) { return false; }
      }

      function sendCtrlV() {
        client.sendKeyEvent(1, 0xFFE3);
        client.sendKeyEvent(1, 0x0076);
        client.sendKeyEvent(0, 0x0076);
        client.sendKeyEvent(0, 0xFFE3);
      }

      var pasteIntercepted = false;
      window.addEventListener('keydown', function(e) {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
          e.stopPropagation();
          e.preventDefault();
          pasteIntercepted = true;
          if (navigator.clipboard && navigator.clipboard.readText) {
            navigator.clipboard.readText().then(function(text) {
              if (text) syncClipboard(text);
              setTimeout(sendCtrlV, 100);
            }).catch(function() { sendCtrlV(); });
          } else {
            sendCtrlV();
          }
        }
      }, true);

      window.addEventListener('keyup', function(e) {
        if (pasteIntercepted && e.key.toLowerCase() === 'v') {
          e.stopPropagation();
          e.preventDefault();
          pasteIntercepted = false;
        }
      }, true);

      document.addEventListener('mousedown', function() {
        if (navigator.clipboard && navigator.clipboard.readText) {
          navigator.clipboard.readText().then(function(text) {
            if (text) syncClipboard(text);
          }).catch(function() {});
        }
      }, true);

      client.onclipboard = function(stream, mimetype) {
        if (mimetype !== 'text/plain') return;
        var reader = new Guacamole.StringReader(stream);
        var data = '';
        reader.ontext = function(text) { data += text; };
        reader.onend = function() {
          window.__workshubRemoteClipboard = data;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(data).catch(function() {});
          }
        };
      };
    } catch(e) {}
  }, 500);
})();
      `;
      iframeDoc.head.appendChild(script);
    } catch (err) {
      console.warn('Could not inject Guacamole iframe fixes:', err);
    }
  }, [focusIframeContent]);

  // Trigger Guacamole resize after panel changes
  useEffect(() => {
    const timer = setTimeout(() => {
      const iframe = guacIframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.dispatchEvent(new Event('resize'));
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [splitRatio, isInstructionsCollapsed]);

  // ═══════════════════════════
  // Render: Loading
  // ═══════════════════════════
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.steps.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No steps found for this workshop.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate(`/instructor/session/${sessionId}`)}
        >
          Back to Session
        </Button>
      </Box>
    );
  }

  const steps = data.steps;
  const currentStep = steps[currentStepIndex];
  const hasEnvironment =
    currentStep.type === 'remote_desktop' ||
    currentStep.type === 'web_app';

  // ═══════════════════════════
  // Render: Main
  // ═══════════════════════════
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* ═══════════ PRESENTATION TOOLBAR (48px) ═══════════ */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          height: 48,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Exit Presentation">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/instructor/session/${sessionId}`)
              }
            >
              <BackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: '#1a5c66' }}
          >
            {data.session.name}
          </Typography>
          <Chip
            label={data.workshop.title}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReactionFeed reactions={reactions} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            Step {currentStepIndex + 1} of {steps.length}
          </Typography>
          <Chip
            icon={
              connectionStatus === 'connected' ? (
                <WifiIcon sx={{ fontSize: 14 }} />
              ) : (
                <WifiOffIcon sx={{ fontSize: 14 }} />
              )
            }
            label={
              connectionStatus === 'connected' ? 'Live' : 'Offline'
            }
            size="small"
            color={
              connectionStatus === 'connected' ? 'success' : 'default'
            }
            variant="outlined"
            sx={{ height: 22 }}
          />
          <Chip
            label={`${data.session.studentCount} student${data.session.studentCount !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
          <Tooltip title={!pipSupported ? 'Your browser does not support this feature (use Chrome or Edge)' : pipOpen ? 'Close floating widget' : 'Pop out live dashboard'}>
            <span>
              <IconButton
                size="small"
                onClick={pipOpen ? closePip : openPip}
                disabled={!pipSupported}
                sx={{ color: pipOpen ? '#2a7b88' : 'inherit' }}
              >
                <PipIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {pipOpen && (
            <Typography
              variant="caption"
              sx={{
                color: '#2a7b88',
                fontWeight: 600,
                fontSize: '0.65rem',
                whiteSpace: 'nowrap',
              }}
            >
              Live dashboard popped out
            </Typography>
          )}
        </Box>
      </Box>

      {/* ═══════════ MAIN CONTENT ═══════════ */}
      <Box
        ref={splitContainerRef}
        sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}
      >
        {/* Drag overlay — prevents iframe stealing mouse events */}
        {isDragging && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              cursor: 'col-resize',
            }}
          />
        )}

        {/* ─── Step Rail (collapsed) OR Instructions Panel ─── */}
        {isInstructionsCollapsed && hasEnvironment ? (
          <StepRail
            steps={steps}
            currentStepIndex={currentStepIndex}
            onStepClick={setCurrentStepIndex}
            onExpand={() => setIsInstructionsCollapsed(false)}
          />
        ) : (
          <Box
            sx={{
              width: hasEnvironment ? `${splitRatio}%` : '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              transition: hasEnvironment
                ? 'width 0.3s ease'
                : undefined,
            }}
          >
            {/* Step navigator header */}
            <Box
              onClick={() => setStepListExpanded(!stepListExpanded)}
              sx={{
                px: 2,
                py: 1,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 44,
                flexShrink: 0,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <DotIcon sx={{ color: '#2a7b88', fontSize: 16 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Step {currentStepIndex + 1}: {currentStep.title}
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {steps.map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor:
                          i === currentStepIndex
                            ? '#2a7b88'
                            : 'grey.300',
                      }}
                    />
                  ))}
                </Box>
                {stepListExpanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
                {hasEnvironment && (
                  <Tooltip title="Collapse instructions (Ctrl+B)">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsInstructionsCollapsed(true);
                      }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Expanded step list */}
            <Collapse in={stepListExpanded}>
              <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                <List dense disablePadding>
                  {steps.map((step, i) => (
                    <ListItemButton
                      key={step.id}
                      selected={i === currentStepIndex}
                      onClick={() => {
                        setCurrentStepIndex(i);
                        setStepListExpanded(false);
                      }}
                      sx={{
                        borderLeft:
                          i === currentStepIndex
                            ? '3px solid #2a7b88'
                            : '3px solid transparent',
                        py: 0.75,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: 11,
                            bgcolor:
                              i === currentStepIndex
                                ? '#2a7b88'
                                : 'grey.200',
                            color:
                              i === currentStepIndex
                                ? 'white'
                                : 'text.secondary',
                          }}
                        >
                          {i + 1}
                        </Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight:
                                i === currentStepIndex ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.8125rem',
                            }}
                          >
                            {step.title}
                          </Typography>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mt: 0.25,
                            }}
                          >
                            {stepTypeIcon[step.type]}
                            {step.estimatedTime && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.6875rem' }}
                              >
                                {step.estimatedTime} min
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Collapse>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                {currentStep.title}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2.5,
                }}
              >
                <Chip
                  icon={stepTypeIcon[currentStep.type] || undefined}
                  label={
                    currentStep.type === 'remote_desktop'
                      ? 'Remote Desktop'
                      : currentStep.type === 'web_app'
                        ? 'Web App'
                        : 'Introduction'
                  }
                  size="small"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
                {currentStep.estimatedTime && (
                  <Chip
                    icon={<TimerIcon sx={{ fontSize: 14 }} />}
                    label={`${currentStep.estimatedTime} min`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 24 }}
                  />
                )}
              </Box>

              {/* Block content */}
              {currentStep.blocks && currentStep.blocks.length > 0 ? (
                <BlockRenderer
                  blocks={currentStep.blocks}
                  interactive={false}
                />
              ) : (
                <>
                  {currentStep.content && (
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        mb: 2,
                        lineHeight: 1.7,
                      }}
                    >
                      {currentStep.content.replace(/\\n/g, '\n')}
                    </Typography>
                  )}
                  {currentStep.instructions && (
                    <Paper
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: '4px solid #2a7b88',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        Instructions
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {currentStep.instructions.replace(
                          /\\n/g,
                          '\n',
                        )}
                      </Typography>
                    </Paper>
                  )}
                </>
              )}

              {/* Target URL hint for remote desktop steps */}
              {currentStep.type === 'remote_desktop' &&
                currentStep.targetUrl && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: '#e3f2fd',
                      borderRadius: 1,
                      border: '1px solid #90caf9',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Navigate to this URL in the remote desktop
                      browser:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                      }}
                    >
                      {currentStep.targetUrl}
                    </Typography>
                  </Box>
                )}
            </Box>

            {/* Navigation buttons */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                gap: 1,
              }}
            >
              {currentStepIndex > 0 && (
                <Button
                  variant="outlined"
                  onClick={() =>
                    setCurrentStepIndex(currentStepIndex - 1)
                  }
                >
                  &#8592; Previous
                </Button>
              )}
              {currentStepIndex < steps.length - 1 && (
                <Button
                  variant="contained"
                  sx={{
                    flex: 1,
                    bgcolor: '#2a7b88',
                    '&:hover': { bgcolor: '#1a5c66' },
                  }}
                  onClick={() =>
                    setCurrentStepIndex(currentStepIndex + 1)
                  }
                >
                  Next &#8594;
                </Button>
              )}
              {currentStepIndex === steps.length - 1 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    fontStyle: 'italic',
                    alignSelf: 'center',
                  }}
                >
                  Last step
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ─── Drag Handle ─── */}
        {hasEnvironment && !isInstructionsCollapsed && (
          <Box
            onMouseDown={handleDragStart}
            onDoubleClick={() =>
              setSplitRatio(currentStep.splitRatio || 35)
            }
            sx={{
              width: 6,
              cursor: 'col-resize',
              bgcolor: isDragging ? 'primary.light' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'primary.light' },
              transition: 'background-color 0.15s',
            }}
          >
            <Box
              sx={{
                width: 2,
                height: 32,
                borderRadius: 1,
                bgcolor: 'grey.300',
              }}
            />
          </Box>
        )}

        {/* ─── Environment Panel ─── */}
        {hasEnvironment && (
          <Box
            sx={{
              width: isInstructionsCollapsed
                ? 'calc(100% - 48px)'
                : `${100 - splitRatio}%`,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: isInstructionsCollapsed
                ? 'none'
                : '1px solid',
              borderColor: 'divider',
              transition: 'width 0.3s ease',
            }}
          >
            {/* Environment header */}
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
              }}
            >
              {currentStep.type === 'remote_desktop' ? (
                <DesktopIcon
                  sx={{ fontSize: 18, color: 'text.secondary' }}
                />
              ) : (
                <WebAppIcon
                  sx={{ fontSize: 18, color: 'text.secondary' }}
                />
              )}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, flex: 1 }}
              >
                {currentStep.environmentLabel ||
                  (currentStep.type === 'remote_desktop'
                    ? 'Remote Desktop'
                    : 'Web Application')}
              </Typography>
              {currentStep.type === 'remote_desktop' && desktopInfo?.assigned && (
                <>
                  <Tooltip title="Copy text from the remote session to your clipboard">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCopyFromRemote}
                      sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12, textTransform: 'none' }}
                    >
                      Copy
                    </Button>
                  </Tooltip>
                  <Tooltip title="Paste text into the remote session">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handlePasteToRemote}
                      sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12, textTransform: 'none' }}
                    >
                      Paste
                    </Button>
                  </Tooltip>
                </>
              )}
            </Box>
            {/* Environment content */}
            <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#1e1e2e' }}>
              {currentStep.type === 'remote_desktop' ? (
                desktopInfo?.assigned ? (
                  <iframe
                    ref={guacIframeRef}
                    src={desktopInfo.embedUrl}
                    title="Instructor Remote Desktop"
                    aria-label="Instructor remote desktop for presentation"
                    tabIndex={0}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    allow="clipboard-read; clipboard-write; keyboard-map"
                    onLoad={handleIframeLoad}
                    onClick={focusIframeContent}
                    onMouseEnter={focusIframeContent}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      bgcolor: 'grey.100',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <DesktopIcon
                      sx={{ fontSize: 48, color: 'grey.400' }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      Remote Desktop Not Available
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {desktopInfo?.error ||
                        'No desktop assigned for instructor.'}
                    </Typography>
                  </Box>
                )
              ) : currentStep.targetUrl ? (
                <iframe
                  src={currentStep.targetUrl}
                  title={currentStep.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    bgcolor: 'grey.100',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <WebAppIcon
                    sx={{ fontSize: 48, color: 'grey.400' }}
                  />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ textAlign: 'center', maxWidth: 400 }}
                  >
                    No application URL configured for this step.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* ═══════════ PiP Floating Widget ═══════════ */}
      {pipWindow && data && (
        <PiPPortal pipWindow={pipWindow}>
          <PiPWidget
            steps={data.steps.map((s) => ({ id: s.id, title: s.title, orderIndex: s.orderIndex }))}
            stepData={stepData}
            totalStudents={totalStudents}
            studentPositions={studentPositions}
            instructorHWM={instructorHWM}
            reactions={pipReactions}
            chatMessages={chatMessages}
            chatUnreadCount={chatUnreadCount}
            onSendChatMessage={handleSendChatMessage}
            onChatViewed={handleChatViewed}
            currentUserId={user?.id || ''}
            onClose={closePip}
          />
        </PiPPortal>
      )}

      {/* ═══════════ Paste Dialog ═══════════ */}
      <Dialog
        open={pasteDialogOpen}
        onClose={() => setPasteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Paste into Remote Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Paste your text below (Ctrl+V), then click Send.
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            variant="outlined"
            placeholder="Paste your text here..."
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                handlePasteDialogSend();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePasteDialogSend}
            disabled={!pasteText}
          >
            Send to Remote
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ Copy Dialog ═══════════ */}
      <Dialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Copy from Remote Session</DialogTitle>
        <DialogContent>
          {remoteClipboardText ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Text copied in the remote desktop:
              </Typography>
              <TextField
                multiline
                minRows={3}
                maxRows={8}
                fullWidth
                variant="outlined"
                value={remoteClipboardText}
                InputProps={{ readOnly: true }}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No text copied yet. Select text in the remote desktop and press Ctrl+C first.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Close</Button>
          {remoteClipboardText && (
            <Button
              variant="contained"
              onClick={handleCopyDialogCopy}
            >
              Copy to Clipboard
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════════ Notification Snackbar ═══════════ */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════
// Step Rail (collapsed sidebar)
// ═══════════════════════════
const StepRail: React.FC<{
  steps: PresentationStep[];
  currentStepIndex: number;
  onStepClick: (index: number) => void;
  onExpand: () => void;
}> = ({ steps, currentStepIndex, onStepClick, onExpand }) => (
  <Box
    sx={{
      width: 48,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'grey.50',
      borderRight: '1px solid',
      borderColor: 'divider',
      alignItems: 'center',
      py: 1,
    }}
  >
    <Tooltip title="Expand instructions (Ctrl+B)" placement="right">
      <IconButton size="small" onClick={onExpand} sx={{ mb: 1 }}>
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Divider sx={{ width: '80%', mb: 1 }} />
    {steps.map((step, idx) => (
      <Tooltip key={step.id} title={step.title} placement="right">
        <Box
          onClick={() => onStepClick(idx)}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0.5,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            transition: 'all 0.2s',
            ...(idx === currentStepIndex
              ? {
                  bgcolor: '#2a7b88',
                  color: 'white',
                  boxShadow: '0 0 0 3px rgba(42,123,136,0.3)',
                }
              : {
                  bgcolor: 'grey.200',
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'grey.300' },
                }),
          }}
        >
          {idx + 1}
        </Box>
      </Tooltip>
    ))}
  </Box>
);

export default PresentationMode;
